import { useState, useEffect, useReducer, useCallback, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import { supabase } from "./supabase";
import { getSiteSettings } from "./api/settings";
import { approveUser as approveProfile } from "./api/profiles";
import brand from "./brand.config";
import { applyFontSource } from "./fonts";
import { CartContext, AuthContext, CartUIContext, AdminContext, BrandContext, SiteSettingsContext } from "./contexts";
import ErrorBoundary from "./components/ErrorBoundary";
import GlobalStyle from "./components/GlobalStyle";
import Nav from "./components/Nav";
import CartModal from "./components/CartModal";
import LandingView from "./views/LandingView";
import ShopView from "./views/ShopView";
import ProductView from "./views/ProductView";
import AuthView from "./views/AuthView";
import CheckoutView from "./views/CheckoutView";
import AccountView from "./views/AccountView";
import ContactView from "./views/ContactView";
import UpdatesView from "./views/UpdatesView";
import MaintenanceView from "./views/MaintenanceView";
import PendingApprovalView from "./views/PendingApprovalView";
import ResetPasswordView from "./views/ResetPasswordView";
import EmailVerificationView from "./views/EmailVerificationView";
import UnsubscribeView from "./views/UnsubscribeView";
import CalculatorView from "./views/CalculatorView";
import { ensureProfile } from "./utils/helpers";
import { cartReducer, syncCartToSupabase, loadCartFromSupabase } from "./cart";
import { registerCheckoutSubscribers } from "./checkout-subscribers";
import { checkForNewRelease } from "./utils/releaseCheck";
import { FavoritesProvider } from "./utils/favorites";
const AdminView = lazy(() => import("./views/AdminView"));

registerCheckoutSubscribers();

// ── Route mapping ────────────────────────────────────────────
const VIEW_PATHS = {
  landing: "/", shop: "/shop", product: "/shop", auth: "/signin", pending: "/pending",
  checkout: "/checkout", account: "/account", admin: "/admin", contact: "/contact",
  updates: "/updates", calculator: "/calculator", "reset-password": "/reset-password", verify: "/verify", maintenance: "/maintenance",
};
const PATH_VIEWS = Object.fromEntries(
  Object.entries(VIEW_PATHS).map(([v, p]) => [p, v])
);

export const rememberPendingProductPath = (path, storage = sessionStorage) => {
  if (/^\/shop\/[^/?#]+$/.test(path || "")) storage.setItem("_pending_product_path", path);
};

export const consumePendingProductPath = (storage = sessionStorage) => {
  const path = storage.getItem("_pending_product_path");
  if (!/^\/shop\/[^/?#]+$/.test(path || "")) return null;
  storage.removeItem("_pending_product_path");
  return path;
};

export const isPasswordRecoveryLink = ({ pathname, search, hash }) => {
  const params = new URLSearchParams(search);
  return hash.includes("type=recovery") || (
    pathname === "/reset-password" &&
    params.get("type") === "recovery" &&
    Boolean(params.get("token_hash"))
  );
};

export const verifySignupConfirmationLink = async (
  { pathname, search },
  auth = supabase.auth,
  history = window.history
) => {
  const params = new URLSearchParams(search);
  const type = params.get("type");
  const token_hash = params.get("token_hash");
  if (pathname !== "/auth/confirm" || !token_hash || !["signup", "email"].includes(type)) {
    return { handled: false, error: null };
  }

  const { error } = await auth.verifyOtp({ token_hash, type });
  params.delete("token_hash");
  params.delete("type");
  const remainingSearch = params.toString();
  history.replaceState({}, "", `${pathname}${remainingSearch ? `?${remainingSearch}` : ""}`);
  return { handled: true, error };
};

// ============================================================
// APP ROOT
// ============================================================
export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

const assignLocation = path => window.location.assign(path);

export function ReleaseWatcher({ check = checkForNewRelease, assign = assignLocation }) {
  const location = useLocation();
  const firstRender = useRef(true);
  const newerReleaseFound = useRef(false);

  useEffect(() => {
    const target = `${location.pathname}${location.search}`;
    if (!firstRender.current && newerReleaseFound.current && location.pathname !== "/checkout") {
      assign(target);
      return;
    }

    firstRender.current = false;
    if (!newerReleaseFound.current) {
      check().then(found => { if (found) newerReleaseFound.current = true; });
    }
  }, [assign, check, location.pathname, location.search]);

  return null;
}

function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cart, dispatch] = useReducer(cartReducer, []);
  const [user, setUser] = useState(null);

  // Derive current "view" from the URL path for components that still reference it
  const pathView = PATH_VIEWS[location.pathname] || (location.pathname.startsWith("/shop/") ? "product" : "landing");
  const view = pathView;

  const setView = useCallback((v, opts) => {
    if (v === "product" && opts?.productId) {
      navigate(`/shop/${opts.productId}`, { replace: opts.replace === true });
    } else if (v === "shop") {
      const pendingPath = consumePendingProductPath();
      if (pendingPath) {
        navigate(pendingPath);
      } else {
        navigate(VIEW_PATHS[v]);
      }
    } else {
      navigate(VIEW_PATHS[v] || "/");
    }
  }, [navigate]);
  const [initialAuthMode, setInitialAuthMode] = useState("signin");
  const [initialAccountTab, setInitialAccountTab] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [appLoading, setAppLoading] = useState(true);
  const [cartLoaded, setCartLoaded] = useState(false);
  const [appliedCoupon, setAppliedCouponRaw] = useState(() => {
    try { const c = localStorage.getItem("_coupon"); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const setAppliedCoupon = (c) => {
    setAppliedCouponRaw(c);
    if (c) localStorage.setItem("_coupon", JSON.stringify(c));
    else localStorage.removeItem("_coupon");
  };
  const [couponToast, setCouponToast] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const confirmationStarted = useRef(false);

  // Preserve a members-only product deep link through the sign-in detour.
  useEffect(() => {
    if (window.location.pathname.startsWith("/shop/") && window.location.pathname !== "/shop") {
      rememberPendingProductPath(window.location.pathname);
    }
  }, []);

  const loadSiteSettings = useCallback(async () => {
    const { data } = await getSiteSettings().catch(() => ({ data: null }));
    if (data) setSiteSettings(data);
    return data;
  }, []);

  // ── Load cart from Supabase when user logs in ───────────────
  useEffect(() => {
    if (!user?.id) { setCartLoaded(false); return; }
    loadCartFromSupabase(user.id).then(items => {
      dispatch({ type: "LOAD", items });
      setCartLoaded(true);
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync cart to Supabase on every change (after initial load) ──
  useEffect(() => {
    if (!user?.id || !cartLoaded) return;
    const t = setTimeout(() => syncCartToSupabase(cart, user.id), 500);
    return () => clearTimeout(t);
  }, [cart, user?.id, cartLoaded]);

  // ── Store campaign ID from URL ?cid= for conversion tracking ─
  useEffect(() => {
    const cid = new URLSearchParams(window.location.search).get("cid");
    if (!cid) return;
    sessionStorage.setItem("_cid", cid);
    const url = new URL(window.location.href);
    url.searchParams.delete("cid");
    window.history.replaceState({}, "", url.toString());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Capture affiliate ref code from URL ?ref=CODE ────────────
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (!ref) return;
    localStorage.setItem("_ref", ref);
    const url = new URL(window.location.href);
    url.searchParams.delete("ref");
    window.history.replaceState({}, "", url.toString());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-apply coupon from URL ?coupon=CODE ──────────────────
  // Save coupon to sessionStorage so it survives login redirect
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("coupon");
    if (code) {
      sessionStorage.setItem("_pending_coupon", code.toUpperCase());
      const url = new URL(window.location.href);
      url.searchParams.delete("coupon");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Apply pending coupon after login (or immediately if already logged in)
  // Uses an interval to keep checking until user is available
  useEffect(() => {
    const code = sessionStorage.getItem("_pending_coupon");
    if (!code) return;
    const tryApply = () => {
      if (!user) return false;
      sessionStorage.removeItem("_pending_coupon");
      supabase.rpc("validate_coupon", { p_code: code })
        .then(({ data }) => {
          if (!data || data.length === 0) return;
          const c = data[0];
          setAppliedCoupon({ code, type: c.coupon_type, value: c.value, min_subtotal: c.min_subtotal, active: true });
          const label = c.coupon_type === "percent" ? `${c.value}% off` : c.coupon_type === "fixed" ? `$${Number(c.value).toFixed(2)} off` : "Free shipping";
          setCouponToast(`Coupon ${code} applied — ${label}`);
          setTimeout(() => setCouponToast(null), 4000);
        });
      return true;
    };
    if (tryApply()) return;
    const interval = setInterval(() => { if (tryApply()) clearInterval(interval); }, 1000);
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle auth errors in URL hash (expired reset links, etc.) ──
  const [authLinkError, setAuthLinkError] = useState(null);
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("error=")) {
      const params = new URLSearchParams(hash.replace("#", ""));
      const errorDesc = params.get("error_description");
      const errorCode = params.get("error_code");
      if (errorDesc) {
        setAuthLinkError(
          errorCode === "otp_expired"
            ? "This link has expired. Please request a new password reset."
            : errorDesc.replace(/\+/g, " ")
        );
        setView("auth");
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Admin settings & user approval state
  const [autoApprove, setAutoApprove] = useState(false);
  const [inventoryManagement, setInventoryManagement] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [appearance, setAppearance] = useState(() => { try { return JSON.parse(localStorage.getItem('_ap') || 'null'); } catch { return null; } });
  const [siteContent, setSiteContent] = useState(() => { try { return JSON.parse(localStorage.getItem('_sc') || 'null'); } catch { return null; } });
  const [pendingUsers, setPendingUsers] = useState([]);

  // ── Supabase session persistence ───────────────────────────
  useEffect(() => {
    const resolveSession = async (session, skipViewChange = false) => {
      if (!session?.user) {
        // Still load public settings before showing the app
        const data = await loadSiteSettings();
        if (data) {
          if (data.appearance) { setAppearance(data.appearance); localStorage.setItem('_ap', JSON.stringify(data.appearance)); }
          if (data.site_content) { setSiteContent(data.site_content); localStorage.setItem('_sc', JSON.stringify(data.site_content)); }
          if (data.maintenance_mode != null) setMaintenanceMode(data.maintenance_mode);
          if (data.auto_approve_signups != null) setAutoApprove(data.auto_approve_signups);
        }
        setAppLoading(false);
        return;
      }
      const resolvedProfile = await ensureProfile(session.user);

      const resolvedUser = {
        id: session.user.id,
        email: session.user.email,
        name: resolvedProfile.name || session.user.email.split("@")[0],
        isAdmin: resolvedProfile.is_admin || false,
        role: resolvedProfile.role || "customer",
        status: resolvedProfile.status || "active",
        marketingOptIn: resolvedProfile.marketing_opt_in || false,
      };

      setUser(resolvedUser);

      // If password recovery is in progress, don't override the view
      if (skipViewChange) {
        // view already set to "reset-password" by onAuthStateChange
      } else {
        // Check URL hash for auth link errors
        const hash = window.location.hash;
        const hashHasError = hash.includes("error=");
        if (hashHasError) {
          navigate("/signin", { replace: true });
        } else {
          // If on landing page or no meaningful route, redirect based on role
          const currentPath = window.location.pathname;
          if (currentPath.startsWith("/shop/") && currentPath !== "/shop") {
            // This session was already authenticated; no sign-in detour remains.
            sessionStorage.removeItem("_pending_product_path");
          }
          const VALID_PATHS = new Set(["/", "/shop", "/account", "/admin", "/checkout", "/contact", "/updates", "/unsubscribe"]);
          const isValidPath = VALID_PATHS.has(currentPath) || currentPath.startsWith("/shop/");
          if (!isValidPath) {
            if (resolvedUser.status === "pending") {
              navigate("/pending", { replace: true });
            } else if (resolvedUser.isAdmin || resolvedUser.role === "manager") {
              navigate("/admin", { replace: true });
            } else {
              navigate("/shop", { replace: true });
            }
          }
          // If on /shop/:id, load the product
          if (currentPath.startsWith("/shop/") && currentPath !== "/shop") {
            const pid = currentPath.split("/shop/")[1];
            if (pid) {
              supabase.from("products").select("*").eq("id", pid).single().then(({ data }) => {
                if (data) setSelectedProduct(data);
                else navigate("/shop", { replace: true });
              });
            }
          }
        }
      }

      // Load all site settings in one query
      const data = await loadSiteSettings();
      if (data) {
        setInventoryManagement(data.inventory_management ?? false);
        setMaintenanceMode(data.maintenance_mode ?? false);
        setAutoApprove(data.auto_approve_signups ?? false);
        if (data.appearance) { setAppearance(data.appearance); localStorage.setItem('_ap', JSON.stringify(data.appearance)); }
        if (data.site_content) { setSiteContent(data.site_content); localStorage.setItem('_sc', JSON.stringify(data.site_content)); }
      }
      setAppLoading(false);
    };

    // Check URL hash for recovery token BEFORE Supabase strips it
    const isRecoveryLink = isPasswordRecoveryLink(window.location);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") { sessionStorage.removeItem("currentView"); sessionStorage.removeItem("accountTab"); sessionStorage.removeItem("adminTab"); setUser(null); setView("landing"); }
      if (event === "PASSWORD_RECOVERY") { setView("reset-password"); }
    });

    const bootstrapSession = async () => {
      if (!confirmationStarted.current) {
        confirmationStarted.current = true;
        const confirmation = await verifySignupConfirmationLink(window.location);
        if (confirmation.handled && confirmation.error) {
          setAuthLinkError("This confirmation link has expired or was already used. Sign in, or sign up again to get a new link.");
          navigate("/signin", { replace: true });
          await resolveSession(null);
          return;
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      await resolveSession(session, isRecoveryLink);
    };

    bootstrapSession();
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // ── Admin: load pending users from Supabase ─────────────────
  useEffect(() => {
    if (!user?.isAdmin && user?.role !== "manager") return;
    supabase
      .from("pending_users")
      .select("*")
      .eq("status", "pending")
      .order("signed_up_at", { ascending: true })
      .then(({ data }) => { if (data) setPendingUsers(data); });
  }, [user?.isAdmin, user?.role]);

  // Both pending-list IDs and profile records use the same approval workflow.
  const approveUser = async (target, { profileUpdates = {}, notification = {} } = {}) => {
    const fromPending = typeof target === "string";
    const record = fromPending ? pendingUsers.find(u => u.id === target) : target;
    if (!record) throw new Error("User to approve was not found.");
    const profileId = fromPending ? record.auth_user_id : record.id;
    await approveProfile(record, { fromPending, profileUpdates, notification });
    setPendingUsers(ps => ps.filter(u => fromPending ? u.id !== record.id : u.auth_user_id !== profileId));
  };

  const rejectUser = async (id) => {
    const record = pendingUsers.find(u => u.id === id);
    if (record?.auth_user_id) {
      await supabase.from("profiles").update({ status: "rejected" }).eq("id", record.auth_user_id);
    }
    await supabase.from("pending_users").update({ status: "rejected" }).eq("id", id);
    setPendingUsers(ps => ps.filter(u => u.id !== id));
    if (record?.email) {
      supabase.functions.invoke("send-approval-notification", {
        body: { to: record.email, userName: record.name ?? record.email, decision: "rejected" },
      }).catch(err => console.error("Rejection email failed:", err));
    }
  };

  // Apply appearance CSS vars directly to <html> — overrides the static :root in GlobalStyle
  useEffect(() => {
    const c = { ...brand.colors, ...(appearance?.colors || {}) };
    const f = { ...brand.fonts, ...(appearance?.fonts || {}) };
    const el = document.documentElement;
    el.style.setProperty('--bg', c.bg);
    el.style.setProperty('--surface', c.surface);
    el.style.setProperty('--surface2', c.surface2);
    el.style.setProperty('--border', c.border);
    el.style.setProperty('--amber', c.accent);
    el.style.setProperty('--amber-dim', c.accentDim);
    el.style.setProperty('--text', c.text);
    el.style.setProperty('--text-dim', c.textDim);
    el.style.setProperty('--text-muted', c.textMuted);
    el.style.setProperty('--red', c.red);
    el.style.setProperty('--green', c.green);
    el.style.setProperty('--radius', c.radius);
    el.style.setProperty('--font-display', f.display);
    el.style.setProperty('--font-mono', f.mono);
    el.style.setProperty('--font-body', f.body);
    // Fonts are self-hosted (src/fonts.js); only a non-Google custom URL gets a <link>.
    const externalFonts = applyFontSource(f.importUrl);
    let link = document.getElementById('google-fonts-link');
    if (externalFonts) {
      if (!link) { link = document.createElement('link'); link.id = 'google-fonts-link'; link.rel = 'stylesheet'; document.head.appendChild(link); }
      link.href = externalFonts;
    } else if (link) link.remove();
  }, [appearance]);

  const b = siteContent ? { ...brand, ...siteContent } : brand;
  const adminContextValue = { autoApprove, setAutoApprove, inventoryManagement, setInventoryManagement, maintenanceMode, setMaintenanceMode, appearance, setAppearance, siteContent, setSiteContent, pendingUsers, approveUser, rejectUser };

  // Hard gate — redirect unauthenticated / pending users
  const GATED_PATHS = new Set(["/shop", "/checkout", "/account", "/admin", "/updates"]);
  const isPending = user?.status === "pending";
  const currentPath = location.pathname;
  const isGated = GATED_PATHS.has(currentPath) || currentPath.startsWith("/shop/");
  let effectiveView = view;
  if (!user && isGated) effectiveView = "landing";
  if (isPending && view !== "pending" && view !== "reset-password") effectiveView = "pending";
  if (maintenanceMode && !user?.isAdmin && user?.role !== "manager" && effectiveView !== "auth" && effectiveView !== "reset-password") effectiveView = "maintenance";

  // After password reset, resolve session and redirect
  const handleResetComplete = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();
      if (profile) {
        setUser({
          id: session.user.id, email: session.user.email,
          name: profile.name || session.user.email.split("@")[0],
          isAdmin: profile.is_admin || false, role: profile.role || "customer", status: profile.status || "active",
          marketingOptIn: profile.marketing_opt_in || false,
        });
        navigate((profile.is_admin || profile.role === "manager") ? "/admin" : "/shop");
        return;
      }
    }
    navigate("/signin");
  };

  // Common props passed to all view components
  const viewProps = { setView, setSelectedProduct, product: selectedProduct, products, setProducts, initialAuthMode, setInitialAuthMode, initialAccountTab, setInitialAccountTab, authLinkError, setAuthLinkError };

  // ProductView wrapper that loads product from URL param
  const ProductRoute = () => {
    const { id } = useParams();
    useEffect(() => {
      if (id && (!selectedProduct || selectedProduct.id !== id)) {
        // The route's own row loads exactly as before (hidden products stay reachable by link);
        // the visible list alongside it feeds the variant selectors.
        Promise.all([
          supabase.from("products").select("*").eq("id", id).single(),
          supabase.from("products").select("*").neq("is_visible", false).order("name"),
        ]).then(([{ data }, { data: visible }]) => {
          if (visible) setProducts(visible);
          if (data) setSelectedProduct(data);
          else navigate("/shop", { replace: true });
        });
      }
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
    return selectedProduct ? <ProductView {...viewProps} product={selectedProduct} /> : null;
  };

  // Determine what to render based on auth gates
  const renderRoutes = () => {
    if (effectiveView === "maintenance") return <MaintenanceView {...viewProps} />;
    if (effectiveView === "pending") return <PendingApprovalView {...viewProps} />;
    if (effectiveView === "landing") {
      return (
        <Routes>
          <Route path="/signin" element={<AuthView {...viewProps} />} />
          <Route path="/contact" element={<ContactView {...viewProps} />} />
          <Route path="/reset-password" element={<ResetPasswordView {...viewProps} onComplete={handleResetComplete} />} />
          <Route path="/unsubscribe" element={<UnsubscribeView />} />
          <Route path="*" element={<LandingView {...viewProps} />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route path="/" element={<LandingView {...viewProps} />} />
        <Route path="/shop" element={<ShopView {...viewProps} />} />
        <Route path="/shop/:id" element={<ProductRoute />} />
        <Route path="/signin" element={<AuthView {...viewProps} />} />
        <Route path="/checkout" element={<CheckoutView {...viewProps} />} />
        <Route path="/account" element={<AccountView {...viewProps} />} />
        <Route path="/admin" element={<Suspense fallback={<div style={{display:"flex",justifyContent:"center",alignItems:"center",height:"60vh",color:brand.colors.textDim}}>Loading admin…</div>}><AdminView {...viewProps} /></Suspense>} />
        <Route path="/contact" element={<ContactView {...viewProps} />} />
        <Route path="/updates" element={<UpdatesView {...viewProps} />} />
        <Route path="/calculator" element={<CalculatorView {...viewProps} />} />
        <Route path="/pending" element={<PendingApprovalView {...viewProps} />} />
        <Route path="/reset-password" element={<ResetPasswordView {...viewProps} onComplete={handleResetComplete} />} />
        <Route path="/verify" element={<EmailVerificationView {...viewProps} />} />
        <Route path="/unsubscribe" element={<UnsubscribeView />} />
        <Route path="*" element={<LandingView {...viewProps} />} />
      </Routes>
    );
  };

  if (appLoading) return (
    <>
      <GlobalStyle />
      <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "var(--bg)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, textAlign: "center", padding: "0 24px", boxSizing: "border-box" }}>
        {(siteContent || appearance) && (
          <p style={{ fontFamily: "var(--font-display)", fontSize: "28px", color: "var(--text)", letterSpacing: "-0.02em" }}>
            {b.logoUrl
              ? <img src={b.logoUrl} alt={b.name} style={{ height: 40, display: "block" }} />
              : <>{b.name} <span style={{ color: "var(--amber)" }}>{b.nameAccent}</span></>
            }
          </p>
        )}
        <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.15em" }}>LOADING…</p>
      </div>
    </>
  );

  return (
    <BrandContext.Provider value={b}>
      <ReleaseWatcher />
      <CartContext.Provider value={{ cart, dispatch, appliedCoupon, setAppliedCoupon }}>
        <AuthContext.Provider value={{ user, setUser }}>
          <FavoritesProvider>
          <CartUIContext.Provider value={{ cartOpen, setCartOpen }}>
            <AdminContext.Provider value={adminContextValue}>
            <SiteSettingsContext.Provider value={{ settings: siteSettings, refresh: loadSiteSettings }}>
              <GlobalStyle />
              {process.env.REACT_APP_ENV === "development" && (
                <>
                  <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, background: "#f59e0b", color: "#000", textAlign: "center", padding: "5px 12px", fontFamily: "var(--font-mono)", fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em" }}>
                    DEVELOPMENT BUILD{process.env.REACT_APP_BUILD_SHA && ` — ${process.env.REACT_APP_BUILD_SHA}`}
                  </div>
                  <div style={{ height: 28 }} />
                </>
              )}
              <Nav view={view} setView={setView} setInitialAccountTab={setInitialAccountTab} />
              <ErrorBoundary>
                {renderRoutes()}
              </ErrorBoundary>
              <CartModal setView={setView} />
              {couponToast && (
                <div style={{
                  position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
                  background: "var(--green)", color: "#fff",
                  padding: "12px 20px", borderRadius: "var(--radius)",
                  fontFamily: "var(--font-mono)", fontSize: "13px", fontWeight: 700,
                  letterSpacing: "0.04em", zIndex: 9999,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
                  animation: "fadeUp 0.2s ease",
                  whiteSpace: "nowrap",
                }}>
                  🎟 {couponToast}
                </div>
              )}
            </SiteSettingsContext.Provider>
            </AdminContext.Provider>
          </CartUIContext.Provider>
          </FavoritesProvider>
        </AuthContext.Provider>
      </CartContext.Provider>
    </BrandContext.Provider>
  );
}
